import React, { useRef } from 'react';
import { Trophy, Fire, TrendUp, Timer, Barbell, Lightning, ShareNetwork, X } from '@phosphor-icons/react';

export default function WorkoutSummary({ workout, sets, elapsed, phase, week, day, user, workoutLogs, dateForDay, onClose }) {
  const cardRef = useRef(null);
  const mins = Math.floor(elapsed / 60);

  // Stats
  const totalSets = sets.filter(s => s.done).length;
  const totalTargetSets = workout.exercises.reduce((sum, ex) => sum + ex.sets, 0);
  const completionPct = totalTargetSets > 0 ? Math.round((totalSets / totalTargetSets) * 100) : 0;
  const totalVolume = sets.reduce((sum, s) => sum + ((s.weight || 0) * (s.reps || 0)), 0);
  const totalReps = sets.reduce((sum, s) => sum + (s.reps || 0), 0);

  // Exercises completed
  const exercisesLogged = new Set(sets.filter(s => s.done).map(s => s.ex)).size;

  // PRs this session
  const newPRs = [];
  const exerciseMaxes = {};
  // Get previous bests
  Object.entries(workoutLogs).forEach(([date, log]) => {
    if (date === dateForDay || !log.sets) return;
    log.sets.forEach(s => {
      if (s.weight > 0) {
        if (!exerciseMaxes[s.ex] || s.weight > exerciseMaxes[s.ex]) {
          exerciseMaxes[s.ex] = s.weight;
        }
      }
    });
  });
  // Check current session
  sets.forEach(s => {
    if (s.weight > 0 && s.done) {
      if (!exerciseMaxes[s.ex] || s.weight > exerciseMaxes[s.ex]) {
        newPRs.push({ ex: s.ex, weight: s.weight, reps: s.reps });
      }
    }
  });

  // Previous session volume for comparison
  const sortedDates = Object.keys(workoutLogs).sort().reverse();
  let prevVolume = 0;
  for (const date of sortedDates) {
    if (date === dateForDay) continue;
    const log = workoutLogs[date];
    if (log.sets && log.sets.length > 0) {
      prevVolume = log.sets.reduce((sum, s) => sum + ((s.weight || 0) * (s.reps || 0)), 0);
      break;
    }
  }
  const volumeChange = prevVolume > 0 ? ((totalVolume - prevVolume) / prevVolume * 100).toFixed(1) : 0;

  // Top exercises by volume
  const exVolumes = {};
  sets.filter(s => s.done).forEach(s => {
    const v = (s.weight || 0) * (s.reps || 0);
    exVolumes[s.ex] = (exVolumes[s.ex] || 0) + v;
  });
  const topExercises = Object.entries(exVolumes).sort((a, b) => b[1] - a[1]).slice(0, 3);

  const shareCard = async () => {
    const text = `LE PROTOCOL — ${workout.name}\nPhase ${phase} / Week ${week} / Day ${day}\n\n${totalSets} sets | ${totalReps} reps | ${(totalVolume/1000).toFixed(1)}t volume\n${mins} min | ${exercisesLogged} exercises${newPRs.length > 0 ? `\n\nNEW PRs: ${newPRs.map(p => `${p.ex} ${p.weight}kg`).join(', ')}` : ''}\n\n#LeProtocol #BeastMode`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'LE PROTOCOL', text });
      } catch (e) { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-[90] flex items-center justify-center p-4 overflow-y-auto" data-testid="workout-summary">
      <div className="w-full max-w-md my-8">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-6 right-6 text-neutral-500 active:scale-90" data-testid="summary-close">
          <X size={28} weight="bold" />
        </button>

        <div ref={cardRef} className="space-y-4">
          {/* Hero */}
          <div className="text-center py-6">
            <div className="text-5xl mb-3">
              {completionPct >= 100 ? '🏆' : completionPct >= 75 ? '💪' : '⚡'}
            </div>
            <div className="font-head text-3xl font-black uppercase tracking-tighter text-white">
              {completionPct >= 100 ? 'DESTROYED IT' : completionPct >= 75 ? 'SOLID SESSION' : 'PROGRESS MADE'}
            </div>
            <div className="text-neutral-500 text-sm mt-1 uppercase tracking-wider">
              {workout.name} — Phase {phase} Week {week}
            </div>
          </div>

          {/* Main stats grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="glass rounded-xl p-4 text-center">
              <Timer size={20} weight="bold" className="text-accent mx-auto mb-1" />
              <div className="font-mono text-2xl font-bold text-white">{mins}<span className="text-sm text-neutral-500">min</span></div>
              <div className="text-[10px] text-neutral-500 uppercase font-bold">Duration</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <Barbell size={20} weight="bold" className="text-brand mx-auto mb-1" />
              <div className="font-mono text-2xl font-bold text-white">{(totalVolume/1000).toFixed(1)}<span className="text-sm text-neutral-500">t</span></div>
              <div className="text-[10px] text-neutral-500 uppercase font-bold">Volume</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <Lightning size={20} weight="bold" className="text-warning mx-auto mb-1" />
              <div className="font-mono text-2xl font-bold text-white">{totalSets}<span className="text-sm text-neutral-500">/{totalTargetSets}</span></div>
              <div className="text-[10px] text-neutral-500 uppercase font-bold">Sets Done</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <Fire size={20} weight="bold" className="text-success mx-auto mb-1" />
              <div className="font-mono text-2xl font-bold text-white">{totalReps}</div>
              <div className="text-[10px] text-neutral-500 uppercase font-bold">Total Reps</div>
            </div>
          </div>

          {/* Volume comparison */}
          {prevVolume > 0 && (
            <div className={`flex items-center justify-center gap-2 p-3 rounded-md border ${
              parseFloat(volumeChange) >= 0
                ? 'bg-success/10 border-success/30 text-success'
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
              <TrendUp size={18} weight="bold" />
              <span className="font-mono text-sm font-bold">
                {parseFloat(volumeChange) >= 0 ? '+' : ''}{volumeChange}% vs last session
              </span>
            </div>
          )}

          {/* New PRs */}
          {newPRs.length > 0 && (
            <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy size={18} weight="bold" className="text-warning" />
                <span className="font-head text-sm font-bold uppercase text-warning">New Personal Records!</span>
              </div>
              {newPRs.map((pr, i) => (
                <div key={i} className="flex justify-between items-center py-1">
                  <span className="text-sm text-white">{pr.ex}</span>
                  <span className="font-mono text-sm font-bold text-warning">{pr.weight}kg x {pr.reps}</span>
                </div>
              ))}
            </div>
          )}

          {/* Top exercises */}
          {topExercises.length > 0 && (
            <div className="glass rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Top Exercises by Volume</div>
              {topExercises.map(([ex, vol], i) => (
                <div key={i} className="flex justify-between items-center py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-neutral-500 w-4">{i + 1}.</span>
                    <span className="text-sm text-white">{ex}</span>
                  </div>
                  <span className="font-mono text-xs text-accent">{(vol/1000).toFixed(1)}t</span>
                </div>
              ))}
            </div>
          )}

          {/* Completion bar */}
          <div className="glass rounded-xl p-4">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-neutral-500 uppercase font-bold">Completion</span>
              <span className="font-mono text-white">{completionPct}%</span>
            </div>
            <div className="h-3 bg-elevated rounded-sm overflow-hidden">
              <div className="h-full rounded-sm transition-all" style={{
                width: `${completionPct}%`,
                backgroundColor: completionPct >= 100 ? '#32D74B' : completionPct >= 75 ? '#FFD60A' : '#FF3B30'
              }} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={shareCard}
              className="flex-1 flex items-center justify-center gap-2 bg-elevated border border-border text-white font-head font-bold uppercase h-14 rounded-md active:scale-95"
              data-testid="share-workout-btn">
              <ShareNetwork size={18} weight="bold" /> Share
            </button>
            <button onClick={onClose}
              className="flex-1 bg-brand text-white font-head font-bold uppercase h-14 rounded-md active:scale-95"
              data-testid="summary-done-btn">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
